import { memo, useState, useMemo } from 'react';
import {
  Box, IconButton, Typography, Stack, Select, MenuItem,
  FormControl, InputLabel, Popover, Slider, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Iconify from '../../../components/iconify';
import { getWidgetDef } from './widgetRegistry';
import { friendlyNameConverter } from '../../../services/localizationService';
import { sensorKeyToPollutant } from './statistics';
import { useTimelineTime } from './TimelineContext';
import { globalToLocalTime } from './timelineSync';

function WidgetSettingsPopover({ anchorEl, open, onClose, settings, widget, recordings, recordingMetadata, onUpdate }) {
  if (!settings || settings.length === 0) return null;

  const getSensorKeysForRecording = (recId, filterAQI) => {
    const rec = recordings.find((r) => r.id === recId);
    if (!rec?.data) return [];
    const keys = Object.keys(rec.data).filter(
      (k) => !['altitude', 'latitude', 'longitude'].some((g) => k.toLowerCase().endsWith(g))
    );
    if (filterAQI) return keys.filter((k) => sensorKeyToPollutant(k) !== null);
    return keys;
  };

  const getAllSensorTypes = () => {
    const types = new Set();
    recordings.forEach((rec) => {
      Object.keys(rec.data || {}).forEach((key) => {
        const sensorType = key.split(':').pop();
        if (!['altitude', 'latitude', 'longitude'].includes(sensorType.toLowerCase())) {
          types.add(sensorType);
        }
      });
    });
    return [...types].sort();
  };

  const recLabel = (r) => recordingMetadata[r.id]?.label || r.name;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Stack spacing={1.5} sx={{ p: 2, minWidth: 260, maxWidth: 320 }}>
        <Typography variant="subtitle2">Settings</Typography>
        {settings.map((field) => {
          if (field.type === 'recording') {
            return (
              <FormControl key={field.key} size="small" fullWidth>
                <InputLabel>{field.label}</InputLabel>
                <Select
                  value={widget[field.key] || ''}
                  label={field.label}
                  onChange={(e) => onUpdate({ [field.key]: e.target.value })}
                >
                  {recordings.map((r) => (
                    <MenuItem key={r.id} value={r.id}>{recLabel(r)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            );
          }

          if (field.type === 'sensor') {
            const depRecId = widget[field.dependsOn] || '';
            const sensorKeys = getSensorKeysForRecording(depRecId, field.filterAQI);
            if (sensorKeys.length === 0 && !depRecId) return null;
            return (
              <FormControl key={field.key} size="small" fullWidth>
                <InputLabel>{field.label}</InputLabel>
                <Select
                  value={widget[field.key] || ''}
                  label={field.label}
                  onChange={(e) => onUpdate({ [field.key]: e.target.value })}
                >
                  {field.allowEmpty && <MenuItem value="">None</MenuItem>}
                  {sensorKeys.map((k) => (
                    <MenuItem key={k} value={k}>{friendlyNameConverter(k)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            );
          }

          if (field.type === 'sensorType') {
            const types = getAllSensorTypes();
            return (
              <FormControl key={field.key} size="small" fullWidth>
                <InputLabel>{field.label}</InputLabel>
                <Select
                  value={widget[field.key] || ''}
                  label={field.label}
                  onChange={(e) => onUpdate({ [field.key]: e.target.value })}
                >
                  {types.map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            );
          }

          if (field.type === 'toggle') {
            return (
              <Box key={field.key}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>{field.label}</Typography>
                <ToggleButtonGroup
                  size="small"
                  value={widget[field.key] || field.default || ''}
                  exclusive
                  onChange={(_, v) => { if (v) onUpdate({ [field.key]: v }); }}
                  fullWidth
                  sx={{ '& .MuiToggleButton-root': { py: 0.25, fontSize: '0.7rem', textTransform: 'none' } }}
                >
                  {field.options.map((opt) => (
                    <ToggleButton key={opt.value} value={opt.value}>{opt.label}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            );
          }

          if (field.type === 'slider') {
            return (
              <Box key={field.key}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">{field.label}</Typography>
                  <Typography variant="caption" fontWeight="bold">{widget[field.key] ?? field.default}</Typography>
                </Stack>
                <Slider
                  size="small"
                  value={widget[field.key] ?? field.default}
                  min={field.min}
                  max={field.max}
                  step={field.step || 1}
                  onChange={(_, v) => onUpdate({ [field.key]: v })}
                />
              </Box>
            );
          }

          return null;
        })}
      </Stack>
    </Popover>
  );
}

function WidgetContainer({
  widget, recordings, recordingMetadata = {}, eventsMap,
  rules, activeRules,
  globalRules, globalActiveRuleIds, tabRules, tabActiveRuleIds,
  onToggleGlobalRule, onToggleTabRule, onCreateRule,
  workspaceNotes, workspaceMarkers, tabNotes, tabMarkers,
  onUpdateNotes, onUpdateMarkers, onUpdateTabNotes, onUpdateTabMarkers,
  onRemove, onUpdate,
  otherTabsRules,
}) {
  const def = getWidgetDef(widget.type);
  const currentTime = useTimelineTime();
  const [settingsAnchor, setSettingsAnchor] = useState(null);

  if (!def) {
    return (
      <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100', borderRadius: 2, gap: 1 }}>
        <Typography color="text.secondary">Widget no longer available</Typography>
        <Typography variant="caption" color="text.disabled">Type "{widget.type}" has been removed</Typography>
        <IconButton size="small" onClick={() => onRemove(widget.id)} sx={{ mt: 0.5 }}>
          <Iconify icon="eva:close-fill" width={18} />
        </IconButton>
      </Box>
    );
  }

  const recording = recordings.find((r) => r.id === widget.recordingId);
  const Component = def.component;

  // Build title: "Widget Name" or "Widget Name — Recording Label"
  const recMeta = recording ? recordingMetadata[recording.id] : null;
  const recColor = recMeta?.color || null;
  const recLabel = recMeta?.label || recording?.name || null;

  // For widgets that select their own recording via settings (not requiresRecording)
  const settingsRecKey = (def.settings || []).find((s) => s.type === 'recording')?.key;
  const settingsRecId = settingsRecKey ? widget[settingsRecKey] : null;
  const settingsRec = settingsRecId ? recordings.find((r) => r.id === settingsRecId) : null;
  const settingsRecMeta = settingsRec ? recordingMetadata[settingsRec.id] : null;
  const settingsRecLabel = settingsRecMeta?.label || settingsRec?.name || null;
  const settingsRecColor = settingsRecMeta?.color || null;

  const displayRecLabel = recLabel || settingsRecLabel;
  const displayRecColor = recColor || settingsRecColor;

  const titleText = displayRecLabel ? `${def.label} — ${displayRecLabel}` : def.label;
  const tooltipText = displayRecLabel
    ? `Widget: ${def.label}\nRecording: ${displayRecLabel}`
    : def.label;

  // Title bar background: 10% recording color tint or grey.50
  const titleBarBg = displayRecColor ? alpha(displayRecColor, 0.1) : 'grey.50';

  // Determine if recording is out of range for the overlay
  const isRecordingBound = def.requiresRecording && recording;
  const localTime = isRecordingBound ? globalToLocalTime(currentTime, recording) : 'in-range';
  const isOutOfRange = isRecordingBound && localTime === null;

  // Build subtitle for out-of-range overlay
  let outOfRangeSubtitle = '';
  if (isOutOfRange && recording) {
    const recName = recMeta?.label || recording.name || 'Recording';
    if (currentTime < recording.startTime) {
      outOfRangeSubtitle = `"${recName}" starts at ${new Date(recording.startTime).toLocaleTimeString()}`;
    } else {
      outOfRangeSubtitle = `"${recName}" ended at ${new Date(recording.endTime).toLocaleTimeString()}`;
    }
  }

  const hasSettings = (def.settings || []).length > 0;

  const handleSettingsUpdate = (updates) => {
    onUpdate(widget.id, updates);
  };

  const widgetProps = {
    recording,
    recordings,
    sensorKey: widget.sensorKey,
    events: widget.recordingId
      ? (eventsMap?.[widget.recordingId] || [])
      : Object.values(eventsMap || {}).flat(),
    rules, activeRules,
    globalRules, globalActiveRuleIds,
    tabRules, tabActiveRuleIds,
    onToggleGlobalRule, onToggleTabRule, onCreateRule,
    workspaceNotes, workspaceMarkers,
    tabNotes, tabMarkers,
    onUpdateNotes, onUpdateMarkers,
    onUpdateTabNotes, onUpdateTabMarkers,
    // Legacy aliases for widgets that don't know about scopes
    onToggleRule: onToggleGlobalRule,
    timelineMarkers: workspaceMarkers,
    widgetConfig: { ...widget, otherTabsRules: otherTabsRules || [] },
    onUpdateWidget: (updates) => onUpdate(widget.id, updates),
    recordingMetadata,
  };

  return (
    <Box sx={{
      height: '100%', display: 'flex', flexDirection: 'column',
      borderRadius: 2,
      overflow: 'hidden',
      bgcolor: 'background.paper',
      boxShadow: 1,
    }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 1, py: 0.5,
          borderBottom: 1, borderColor: 'divider',
          bgcolor: titleBarBg,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
          {displayRecColor && (
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: displayRecColor, flexShrink: 0 }} />
          )}
          <Iconify icon={def.icon} width={18} sx={{ flexShrink: 0, color: 'text.secondary' }} />
          <Typography variant="body2" noWrap title={tooltipText} sx={{ fontWeight: 500, lineHeight: 1.4 }}>
            {titleText}
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={0.25}>
          {hasSettings && (
            <IconButton
              size="small"
              onClick={(e) => setSettingsAnchor(e.currentTarget)}
              sx={{
                opacity: 0.5,
                '&:hover': { opacity: 1 },
                transition: 'opacity 0.15s',
              }}
            >
              <Iconify icon="eva:settings-2-outline" width={16} />
            </IconButton>
          )}

          <IconButton
            size="small"
            onClick={() => onRemove(widget.id)}
            sx={{
              opacity: 0.5,
              '&:hover': { opacity: 1, bgcolor: 'error.lighter', color: 'error.main' },
              transition: 'all 0.15s',
            }}
          >
            <Iconify icon="eva:close-fill" width={16} />
          </IconButton>
        </Stack>
      </Stack>

      <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        {def.requiresRecording && !recording ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography color="text.secondary" variant="body2">Recording removed</Typography>
          </Box>
        ) : (
          <>
            <Component {...widgetProps} />
            {isOutOfRange && (
              <Box sx={{
                position: 'absolute', inset: 0,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                bgcolor: 'rgba(0,0,0,0.5)', zIndex: 5,
              }}>
                <Typography color="white" variant="body2">Widget isn&apos;t applicable at this time</Typography>
                {outOfRangeSubtitle && (
                  <Typography color="grey.400" variant="caption" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                    {outOfRangeSubtitle}
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}
      </Box>

      <WidgetSettingsPopover
        anchorEl={settingsAnchor}
        open={Boolean(settingsAnchor)}
        onClose={() => setSettingsAnchor(null)}
        settings={def.settings || []}
        widget={widget}
        recordings={recordings}
        recordingMetadata={recordingMetadata}
        onUpdate={handleSettingsUpdate}
      />
    </Box>
  );
}

export default memo(WidgetContainer);
